// The application: one shell, one screen at a time.
//
// Routing is state rather than a router, on purpose and for now. A URL scheme
// is worth designing once the screens that need deep links (a document, an
// operation) are all here, rather than being invented twice. The one thing that
// must not wait is the context rule from V03 — the selected administration
// outlives navigation, which it does because it lives in WorkspaceProvider,
// above this component.
//
// The agency screens are the exception to "one office at a time", and they do
// not change the workspace to be it: the office an operator is working on there
// is state of THIS component, so moving between offices to prepare a test does
// not silently repoint every customer screen. See apps/web/src/agency.ts.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ALL_NAV, NotBuiltYet, Shell, type ScreenId } from './components/Shell.tsx';
import { LocaleProvider } from './i18n/index.tsx';
import { SignIn } from './components/SignIn.tsx';
import { ActivityScreen } from './screens/Activity.tsx';
import { AskFleurScreen } from './screens/AskFleur.tsx';
import { AdministrationsScreen } from './screens/Administrations.tsx';
import { AgencyOfficesScreen } from './screens/AgencyOffices.tsx';
import { AgencyOnboardingScreen } from './screens/AgencyOnboarding.tsx';
import { AgencyUsageScreen } from './screens/AgencyUsage.tsx';
import { AgencyWorkspaceScreen } from './screens/AgencyWorkspace.tsx';
import { BankMatchingScreen, type AskSubject } from './screens/BankMatching.tsx';
import { ConnectionsScreen } from './screens/Connections.tsx';
import { CustomerCommunicationScreen } from './screens/CustomerCommunication.tsx';
import { DocumentsScreen, type DocumentsView } from './screens/Documents.tsx';
import { TodayScreen } from './screens/Today.tsx';
import { WorkingRulesScreen } from './screens/WorkingRules.tsx';
import { WorkQueueScreen } from './screens/WorkQueue.tsx';
import type { WorkQueue } from './api/types.ts';
import { openWorkTotal } from './state/openWork.ts';
import { filterQuery, useRead } from './state/useRead.ts';
import { administrationFilter, useWorkspace, WorkspaceProvider } from './state/workspace.tsx';

/** How often the work-queue badge is refreshed while nothing else asks for it. */
export const OPEN_WORK_POLL_MS = 30_000;

export function App(): ReactNode {
  return (
    <LocaleProvider>
      <WorkspaceProvider
        renderSignIn={({ onToken, error, busy }) => (
          <SignIn onToken={onToken} error={error} busy={busy} />
        )}
      >
        <Workspace />
      </WorkspaceProvider>
    </LocaleProvider>
  );
}


/** The signed-in app: the shell and the screen it shows. Exported for the click-through test. */
export function Workspace(): ReactNode {
  const workspace = useWorkspace();
  const [screen, setScreen] = useState<ScreenId>('today');
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [documentsView, setDocumentsView] = useState<DocumentsView>('open');
  // The open work, read here rather than on one screen: the sidebar badge is
  // live on every page, and Today's count, list and KPI are this same answer.
  // Re-read on every navigation, so a decision made on one screen is counted
  // by the time the next one opens.
  const work = useRead<WorkQueue>(
    workspace.membership.mfaSatisfied ? `/work${filterQuery(administrationFilter(workspace.administrationId))}` : null,
    'the work queue',
    { pollMs: OPEN_WORK_POLL_MS },
  );
  const reloadWork = work.reload;
  useEffect(() => {
    void reloadWork();
  }, [screen, reloadWork]);
  // A source one screen was looking at, handed to the screen that can ask a
  // customer about it. It lives here rather than in either screen because it
  // is the thing that crosses between them.
  const [askSubject, setAskSubject] = useState<AskSubject | null>(null);
  // The office the agency screens are working on. Deliberately NOT the
  // workspace's selected office: an operator moving between offices to look at
  // a release should not be repointing every customer screen behind them. The
  // one place that does change the workspace says so on the control that does
  // it — see the Office workspace screen.
  const [agencyTenantId, setAgencyTenantId] = useState<string | null>(null);

  const openDocument = useCallback((id: string) => {
    setDocumentId(id);
    setScreen('documents');
  }, []);

  /** The sidebar, and every "go to that page" link: the page as it opens by default. */
  const navigate = useCallback((next: ScreenId) => {
    if (next === 'documents') setDocumentsView('open');
    setScreen(next);
  }, []);

  /** Documents, showing what was booked this month. */
  const openBooked = useCallback(() => {
    setDocumentsView('booked');
    setDocumentId(null);
    setScreen('documents');
  }, []);

  const askAbout = useCallback((subject: AskSubject) => {
    setAskSubject(subject);
    setScreen('messages');
  }, []);

  return (
    <Shell screen={screen} onNavigate={navigate} openWork={openWorkTotal(work.data)} onOpenDocument={openDocument}>
      {screen === 'today' ? (
        <TodayScreen work={work} onOpenDocument={openDocument} onOpenBooked={openBooked} onNavigate={navigate} />
      ) : screen === 'documents' ? (
        <DocumentsScreen
          selectedId={documentId}
          onSelect={setDocumentId}
          view={documentsView}
          onView={setDocumentsView}
        />
      ) : screen === 'bank' ? (
        <BankMatchingScreen onAskForDocument={askAbout} />
      ) : screen === 'work' ? (
        <WorkQueueScreen onOpenDocument={openDocument} onNavigate={navigate} />
      ) : screen === 'administrations' ? (
        <AdministrationsScreen onNavigate={navigate} />
      ) : screen === 'connections' ? (
        <ConnectionsScreen />
      ) : screen === 'rules' ? (
        <WorkingRulesScreen onOpenDocument={openDocument} onNavigate={navigate} />
      ) : screen === 'messages' ? (
        <CustomerCommunicationScreen
          subject={askSubject}
          onSubjectHandled={() => setAskSubject(null)}
          onOpenDocument={openDocument}
          onNavigate={navigate}
        />
      ) : screen === 'activity' ? (
        <ActivityScreen />
      ) : screen === 'ask' ? (
        <AskFleurScreen onOpenDocument={openDocument} onNavigate={navigate} />
      ) : screen === 'agency-offices' ? (
        <AgencyOfficesScreen onOpenOffice={setAgencyTenantId} onNavigate={navigate} />
      ) : screen === 'agency-onboarding' ? (
        <AgencyOnboardingScreen />
      ) : screen === 'agency-usage' ? (
        <AgencyUsageScreen />
      ) : screen === 'agency-workspace' ? (
        <AgencyWorkspaceScreen
          tenantId={agencyTenantId}
          onSelectOffice={setAgencyTenantId}
          onNavigate={navigate}
        />
      ) : (
        <NotBuiltYet label={ALL_NAV.find((entry) => entry.id === screen)?.label ?? screen} />
      )}
    </Shell>
  );
}
